import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { ChevronLeftIcon } from '@sketchnote/icons';
import cn from 'classnames';
import { format } from 'date-fns';
import castArray from 'lodash/castArray';
import * as React from 'react';
import type { CalendarProps as ReactCalendarProps } from 'react-calendar';
import ReactCalendar, { OnChangeDateRangeCallback } from 'react-calendar';

import styles from './dashboardcalendar.module.css';

const { useState } = React;
type CalendarTabs = 'date' | 'startEndDate';

interface CalendarProps {
  value: [Date];
  onChange: OnChangeDateRangeCallback;
  onDone: () => void;
  configs?: ReactCalendarProps;
  show?: CalendarTabs[];
  disableEndDate?: boolean;
}

const Calendar = ({ value, onChange, onDone, configs }: CalendarProps) => {
  const handleChangeNew = (value: [Date], e: React.ChangeEvent<HTMLInputElement>) => {
    const dates = castArray(value) as [Date];
    if (onChange) {
      onChange(dates, e);
      onDone();
    }
  };

  return (
    <div>
      <section className={styles['calendar-body']}>
        <ReactCalendar
          className={styles.calendar}
          onChange={handleChangeNew}
          allowPartialRange={true}
          value={value}
          nextLabel={
            <ChevronLeftIcon style={{ transform: 'rotate(180deg) translate(0px,-4px)' }} />
          }
          prevLabel={<ChevronLeftIcon style={{ transform: 'rotate(180deg)' }} />}
          prev2Label={null}
          next2Label={null}
          minDetail="month"
          maxDetail="month"
          defaultView="month"
          formatMonth={formatMonth}
          formatShortWeekday={formatDay}
          maxDate={new Date(2049, 12, 31)}
          minDate={new Date(2001, 1, 1)}
          {...configs}
        />
      </section>
    </div>
  );
};
const formatMonth: ReactCalendarProps['formatMonth'] = (locale, date) => {
  return date.toLocaleString('default', { month: 'short' });
};

const formatDay: ReactCalendarProps['formatDay'] = (locale, date) => {
  return date.toDateString().slice(0, 3);
};

interface DateRangePickerProps {
  value: [Date];
  onChange: OnChangeDateRangeCallback;
  onDone?: () => void;
  inputClassName?: string;
  placeholderText?: string;
  dateFormat?: string;
  initialTab?: CalendarTabs;
  readOnly?: boolean;
  show?: CalendarProps['show'];
  disableEndDate?: CalendarProps['disableEndDate'];
}

export const DashboardCalendar = ({
  value,
  onChange,
  onDone,
  inputClassName,
  dateFormat = 'do MMM yyyy',
  readOnly = false,
  show,
  disableEndDate = false,
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  const handleDoneClick = () => {
    if (onDone) onDone();
    setIsOpen(false);
  };

  return (
    <Popover isOpen={readOnly ? false : isOpen} onClose={close} onOpen={open}>
      <PopoverTrigger>
        <button
          className={cn({
            'text-gray-500': !value,
            [inputClassName || '']: !!inputClassName,
          })}
        >
          {format(value[0], dateFormat)}
        </button>
      </PopoverTrigger>
      <PopoverContent zIndex={4}>
        <Calendar
          value={value}
          onChange={onChange}
          onDone={handleDoneClick}
          show={show}
          disableEndDate={disableEndDate}
        />
      </PopoverContent>
    </Popover>
  );
};
